import { Fragment, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { APP_BAR_HEIGHT } from '../components/layout';
import { safeDecode } from '../utils/decode';
import { groupQuotas, percent, progressState, sortGroups } from './quotaGroups';
import { quotaKey, SYSTEM_COLOURS, SYSTEM_LABELS } from './trackerSeries';

const STATE_COLOURS = { progress: 'primary', full: 'success', over: 'warning' };
export const numeric = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

// The quota tables' shared look (monitoring and tracker): a sticky header below the app bar, and
// pairs of rows (a quota's Alchemer and Wrangler rows) shaded alternately, with a solid line
// between quotas and a dashed one within a pair.
export const headCellSx = (col, i, count) => ({
  top: APP_BAR_HEIGHT,
  bgcolor: 'grey.100',
  fontWeight: 600,
  py: 1.25,
  px: 1.5,
  minWidth: col.minWidth,
  width: col.width,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  ...(col.numeric && numeric),
  ...(i === 0 && { borderTopLeftRadius: 8 }),
  ...(i === count - 1 && { borderTopRightRadius: 8 }),
});

export const pairCellSx = (lastInPair) => ({
  py: 1.25,
  px: 1.5,
  borderBottom: lastInPair ? '1px solid' : '1px dashed',
  borderColor: lastInPair ? 'divider' : 'grey.200',
});

export const pairRowSx = (groupIndex, finalRow) => ({
  bgcolor: groupIndex % 2 ? '#f4f6f8' : 'background.paper',
  '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
  ...(finalRow && { '& > td': { borderBottom: 'none' } }),
});

export const tableContainerSx = { border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'visible' };

/** Errors for systems that failed. A failed system's quotas are missing, never shown as zeros. */
export const SystemAlerts = ({ systems }) => (
  <>
    {Object.entries(systems || {})
      .filter(([, s]) => s.status === 'error')
      .map(([system, s]) => (
        <Alert key={system} severity="warning" sx={{ mb: 2 }}>
          {SYSTEM_LABELS[system] || system} quotas are missing: {s.error}
        </Alert>
      ))}
  </>
);

// Every chip in the table: the same size and type, coloured by meaning.
const chipSx = { height: 22, fontSize: 12, fontWeight: 600, borderRadius: 1.5, '& .MuiChip-label': { px: 1 } };

export const SystemChip = ({ system }) => (
  <Chip
    size="small"
    variant="outlined"
    label={SYSTEM_LABELS[system] || system}
    icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SYSTEM_COLOURS[system], ml: '8px !important' }} />}
    sx={{ ...chipSx, borderColor: 'divider', color: 'text.primary', bgcolor: 'background.paper' }}
  />
);

/** A soft status chip: tinted background, dark text, in the palette colour of its meaning. */
export const StatusChip = ({ label, colour, title }) => (
  <Tooltip title={title || ''}>
    <Chip
      size="small"
      label={label}
      sx={(theme) => {
        const palette = colour === 'grey'
          ? { main: theme.palette.grey[600], dark: theme.palette.grey[800] }
          : theme.palette[colour];
        return { ...chipSx, bgcolor: alpha(palette.main, 0.14), color: palette.dark };
      }}
    />
  </Tooltip>
);

const StatusChips = ({ quota }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
    {quota.is_on === false && <StatusChip label="OFF" colour="grey" title="Not blocking respondents" />}
    {quota.closed && <StatusChip label="Closed" colour="error" title="Full, counting places held" />}
    {quota.needs_review && <StatusChip label="Needs review" colour="warning" title="Imported from Alchemer and not reviewed yet" />}
    {quota.group_join_unknown && <StatusChip label="AND/OR needed" colour="warning" title="Choose how its groups combine before switching it on" />}
  </Box>
);

const progressTooltip = (quota, value) => {
  if (value === null) return `${quota.responses} completes; no limit`;
  const base = `${quota.responses} completes of ${quota.limit} (${Math.round(value)}%)`;
  if (!quota.reserved) return base;
  const withPlaces = ((quota.responses + quota.reserved) / quota.limit) * 100;
  return `${quota.responses} completes + ${quota.reserved} holding a place, of ${quota.limit}: `
    + `${Math.round(value)}%, ${Math.round(withPlaces)}% counting places held`;
};

/**
 * A thick bar: completes in the state's colour (in progress, full, over the limit), then for
 * Wrangler quotas the places held as a lighter segment of the same colour.
 */
export const ProgressBar = ({ quota }) => {
  const value = percent(quota);
  const colour = STATE_COLOURS[progressState(quota)];
  const done = value === null ? 0 : Math.min(value, 100);
  const held = value === null || !quota.reserved ? 0
    : Math.min((quota.reserved / quota.limit) * 100, 100 - done);
  return (
    <Tooltip title={progressTooltip(quota, value)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 110, height: 10, borderRadius: 5, bgcolor: 'grey.200', overflow: 'hidden', display: 'flex' }}>
          <Box sx={{ width: `${done}%`, bgcolor: `${colour}.main` }} />
          <Box sx={(theme) => ({ width: `${held}%`, bgcolor: alpha(theme.palette[colour].main, 0.35) })} />
        </Box>
        <Typography variant="body2" sx={{ ...numeric, minWidth: 44, fontWeight: 600, color: colour === 'primary' ? 'text.primary' : `${colour}.dark` }}>
          {value === null ? '—' : `${Math.round(value)}%`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Quota progress from one or both systems. `quotas` carry `system`; Wrangler quotas may carry
 * reserved, is_on, closed and review flags, which get their own columns when present. A quota's
 * Alchemer and Wrangler rows stay together, and alternate quotas are shaded, so each pair reads
 * as one. The table grows with the page; its header sticks below the app bar.
 */
const QuotaTable = ({ quotas }) => {
  const [searchField, setSearchField] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [systemFilter, setSystemFilter] = useState('all');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  const systemsPresent = [...new Set(quotas.map((q) => q.system))];
  const mixed = systemsPresent.length > 1;
  // Partners get only the common fields, so Wrangler's extra columns appear only with the data.
  const hasWrangler = quotas.some((q) => q.system === 'wrangler' && 'reserved' in q);

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const groups = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const shown = quotas.filter((q) => {
      if (systemFilter !== 'all' && q.system !== systemFilter) return false;
      const val = searchField === 'name' ? safeDecode(q.name) : (q.description || '');
      return val.toLowerCase().includes(query);
    });
    return sortGroups(groupQuotas(shown), orderBy, order);
  }, [quotas, searchField, searchQuery, systemFilter, orderBy, order]);

  const columns = [
    ...(mixed ? [{ id: 'system', label: 'System' }] : []),
    { id: 'name', label: 'Name', minWidth: 110 },
    { id: 'description', label: 'Description' },
    { id: 'responses', label: 'Completes', numeric: true },
    ...(hasWrangler ? [{ id: 'reserved', label: 'Holding a place', numeric: true,
      tooltip: 'Wrangler only: respondents in progress who have been let through and hold a place in this quota.' }] : []),
    { id: 'limit', label: 'Limit', numeric: true },
    { id: 'progress', label: 'Progress', minWidth: 180 },
    ...(hasWrangler ? [{ id: 'status', label: 'Status', unsortable: true }] : []),
  ];

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Search" size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Search By</InputLabel>
          <Select value={searchField} label="Search By" onChange={(e) => setSearchField(e.target.value)}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="description">Description</MenuItem>
          </Select>
        </FormControl>
        {mixed && (
          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>System</InputLabel>
            <Select value={systemFilter} label="System" onChange={(e) => setSystemFilter(e.target.value)}>
              <MenuItem value="all">Both</MenuItem>
              {systemsPresent.map((s) => <MenuItem key={s} value={s}>{SYSTEM_LABELS[s] || s}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* overflow visible: a scrolling container would hold the sticky header inside it. */}
      <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col, i) => (
                <TableCell key={col.id} sx={headCellSx(col, i, columns.length)}>
                  {col.unsortable ? col.label : (
                    <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}>
                      {col.tooltip ? <Tooltip title={col.tooltip}><span>{col.label}</span></Tooltip> : col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No quotas match.
                </TableCell>
              </TableRow>
            )}
            {groups.map((group, g) => (
              <Fragment key={quotaKey(group[0])}>
                {group.map((quota, r) => {
                  const last = r === group.length - 1;
                  const cell = pairCellSx(last);
                  return (
                    <TableRow key={quotaKey(quota)} sx={pairRowSx(g, groups.length - 1 === g && last)}>
                      {mixed && <TableCell sx={cell}><SystemChip system={quota.system} /></TableCell>}
                      <TableCell sx={{ ...cell, fontWeight: r === 0 ? 500 : 400, color: r === 0 ? 'text.primary' : 'grey.600' }}>
                        {safeDecode(quota.name)}
                      </TableCell>
                      <TableCell sx={{ ...cell, color: 'grey.700' }}>{quota.description || '—'}</TableCell>
                      <TableCell sx={{ ...cell, ...numeric }}>{quota.responses}</TableCell>
                      {hasWrangler && <TableCell sx={{ ...cell, ...numeric }}>{quota.reserved ?? ''}</TableCell>}
                      <TableCell sx={{ ...cell, ...numeric }}>{quota.limit}</TableCell>
                      <TableCell sx={cell}><ProgressBar quota={quota} /></TableCell>
                      {hasWrangler && <TableCell sx={cell}><StatusChips quota={quota} /></TableCell>}
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default QuotaTable;
